import {
  DragDropContext,
  Draggable,
  Droppable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import {
  ArrowLeft,
  ArrowRight,
  Copy,
  FileText,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react";
import { useDataProvider, useGetList, useNotify } from "ra-core";
import { useState } from "react";
import type React from "react";
import { Link } from "react-router";

import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toSlug } from "@/lib/toSlug";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import { getResolvedWorkspace } from "../providers/supabase/workspaceConfig";
import type { Contact, SlateForm, SlateFormField } from "../types";
import { shortDate } from "./assetActivity";
import { useFormActivity } from "./useFormActivity";
import { EmptyState } from "../misc/EmptyState";

/**
 * Form builder - build a lead-capture form from contact fields (core +
 * custom), reorder by drag and drop, and generate self-contained embed code
 * that posts to the tenant's public form endpoint - computed from the
 * resolved workspace, same as the tracking snippet and Calendly webhook URL,
 * not left as placeholder copy. Submissions land on the event spine as
 * form.submitted (services: POST /f/:tenant/:formId) with first/last-touch
 * attribution read from the Obvious tracking cookie; the automation worker's
 * identity pass then matches or creates the contact and re-emits the event
 * with contactId set, which is what actually fires the "Form is submitted"
 * workflow trigger.
 */

const FIELD_SOURCES = [
  { source: "first_name", label: "First name", type: "text" },
  { source: "last_name", label: "Last name", type: "text" },
  { source: "email", label: "Email", type: "email" },
  { source: "phone", label: "Phone", type: "phone" },
  { source: "company", label: "Company", type: "text" },
  { source: "title", label: "Job title", type: "text" },
  { source: "message", label: "Message", type: "textarea" },
  { source: "consent", label: "Consent checkbox", type: "checkbox" },
  { source: "lead_source", label: "Hidden field", type: "hidden" },
  { source: "pagebreak", label: "-- Page break --", type: "pagebreak" },
] as const;

let uid = Date.now() % 100000;
const nextUid = () => String(++uid);

export const generateEmbedCode = (
  form: SlateForm,
  endpointBase: string,
  tenantSlug: string,
  turnstileSiteKey?: string,
) => {
  const captchaOn = !!form.captcha && !!turnstileSiteKey;
  const design = form.design ?? {};
  const btnBg = design.buttonColor ?? "#334155";
  const btnFg = design.buttonTextColor ?? "#ffffff";
  const radius = design.inputRadius ?? 4;
  const fontCss =
    design.font === "system"
      ? "font-family:system-ui,sans-serif;"
      : design.font === "georgia"
        ? "font-family:Georgia,serif;"
        : ""; // inherit: take the host site's font
  const inputStyle = `width:100%;padding:8px;border:1px solid #cbd5e1;border-radius:${radius}px;box-sizing:border-box;`;

  const renderField = (f: SlateForm["fields"][number]) => {
    const name = f.source;
    const req = f.required ? " required" : "";
    const ph = f.placeholder ? ` placeholder="${f.placeholder}"` : "";
    const label = `      <label for="slate-${f.id}" style="display:block;margin:12px 0 4px;font-size:14px;">${f.label}${f.required ? " *" : ""}</label>`;
    switch (f.type) {
      case "hidden":
        return `      <input type="hidden" name="${name}" value="${f.value ?? ""}" />`;
      case "textarea":
        return `${label}\n      <textarea id="slate-${f.id}" name="${name}" rows="4" style="${inputStyle}"${ph}${req}></textarea>`;
      case "select": {
        const opts = (f.options ?? "")
          .split(",")
          .map((o) => o.trim())
          .filter(Boolean)
          .map((o) => `        <option>${o}</option>`)
          .join("\n");
        return `${label}\n      <select id="slate-${f.id}" name="${name}" style="${inputStyle}"${req}>\n        <option value="">Choose…</option>\n${opts}\n      </select>`;
      }
      case "checkbox":
        return `      <label style="display:flex;gap:8px;align-items:flex-start;margin:12px 0 4px;font-size:13px;"><input type="checkbox" id="slate-${f.id}" name="${name}" value="yes"${req} style="margin-top:2px;" /> <span>${f.label}${f.required ? " *" : ""}</span></label>`;
      default: {
        const inputType =
          f.type === "email" ? "email" : f.type === "phone" ? "tel" : "text";
        return `${label}\n      <input id="slate-${f.id}" type="${inputType}" name="${name}" style="${inputStyle}"${ph}${req} />`;
      }
    }
  };

  // Split fields into pages on pagebreak markers
  const pages: SlateForm["fields"][] = [[]];
  for (const f of form.fields) {
    if (f.type === "pagebreak") pages.push([]);
    else pages[pages.length - 1]!.push(f);
  }
  const multiPage = pages.length > 1;

  const pageHtml = pages
    .map(
      (fields, i) => `    <div data-slate-page="${i}"${i > 0 ? ' style="display:none;"' : ""}>
${fields.map(renderField).join("\n")}
${
  multiPage
    ? `      <div style="display:flex;gap:8px;margin-top:16px;">${
        i > 0
          ? `<button type="button" data-slate-back style="padding:10px 20px;background:transparent;color:${btnBg};border:1px solid ${btnBg};border-radius:${radius}px;cursor:pointer;">Back</button>`
          : ""
      }${
        i < pages.length - 1
          ? `<button type="button" data-slate-next style="padding:10px 20px;background:${btnBg};color:${btnFg};border:none;border-radius:${radius}px;cursor:pointer;">Next</button>`
          : `<button type="submit" style="padding:10px 20px;background:${btnBg};color:${btnFg};border:none;border-radius:${radius}px;cursor:pointer;">${form.submitLabel}</button>`
      }</div>`
    : ""
}
    </div>`,
    )
    .join("\n");

  const captchaWidget = captchaOn
    ? `\n    <div class="cf-turnstile" data-sitekey="${turnstileSiteKey}" style="margin-top:12px;"></div>`
    : "";
  const captchaScript = captchaOn
    ? `\n<script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer></script>`
    : "";
  const afterSubmit = form.redirectUrl
    ? `location.href = "${form.redirectUrl}";`
    : `root.innerHTML = '<p style="font-size:15px;">${form.successMessage.replace(/'/g, "\\'")}</p>';`;
  const singleSubmit = multiPage
    ? ""
    : `\n    <button type="submit" style="margin-top:16px;padding:10px 20px;background:${btnBg};color:${btnFg};border:none;border-radius:${radius}px;cursor:pointer;">${form.submitLabel}</button>`;

  return `<!-- Obvious form: ${form.name} -->${captchaScript}
<div id="slate-form-${form.id}" style="max-width:480px;${fontCss}">
  <form>
${pageHtml}
    <!-- Honeypot: invisible to humans, catches bots. Do not remove. -->
    <input type="text" name="website" tabindex="-1" autocomplete="off" style="position:absolute;left:-9999px;" aria-hidden="true" />${captchaWidget}${singleSubmit}
  </form>
</div>
<script>
(function () {
  var root = document.getElementById("slate-form-${form.id}");
  var form = root.querySelector("form");
  var pages = root.querySelectorAll("[data-slate-page]");
  var current = 0;
  function show(i) {
    for (var p = 0; p < pages.length; p++) pages[p].style.display = p === i ? "" : "none";
    current = i;
  }
  root.addEventListener("click", function (e) {
    if (e.target.hasAttribute && e.target.hasAttribute("data-slate-next")) {
      var ok = true;
      pages[current].querySelectorAll("input,select,textarea").forEach(function (el) {
        if (!el.checkValidity()) { el.reportValidity(); ok = false; }
      });
      if (ok) show(current + 1);
    }
    if (e.target.hasAttribute && e.target.hasAttribute("data-slate-back")) show(current - 1);
  });
  var touch = {};
  try { touch = JSON.parse(decodeURIComponent((document.cookie.match(/(?:^|; )slate_touch=([^;]*)/) || [])[1] || "")) || {}; } catch (e) {}
  form.addEventListener("submit", function (e) {
    e.preventDefault();
    var fields = {};
    var honeypot = "";
    var captchaToken = "";
    new FormData(form).forEach(function (v, k) {
      if (k === "website") honeypot = v;
      else if (k === "cf-turnstile-response") captchaToken = v;
      else fields[k] = v;
    });
    // One id per submission: the server ignores a repeat, so retrying after
    // a network error or a 503 never records the lead twice.
    var submissionId = window.crypto && crypto.randomUUID ? crypto.randomUUID() : Date.now() + "-" + Math.random().toString(36).slice(2);
    var body = JSON.stringify({ fields: fields, touch: touch, pageUrl: location.href, website: honeypot, captchaToken: captchaToken, submissionId: submissionId });
    function send(attempt) {
      function retry() {
        if (attempt >= 3) return;
        return new Promise(function (r) { setTimeout(r, attempt * 2000); }).then(function () { return send(attempt + 1); });
      }
      return fetch("${endpointBase}/f/${tenantSlug}/${form.id}", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body,
      }).then(function (res) { if (res.status >= 500) return retry(); }, retry);
    }
    send(1).finally(function () {
      ${afterSubmit}
    });
  });
})();
</script>`;
};

/** Small stat tile shared by the performance views. */
const StatTile = ({ label, value, detail }: { label: string; value: string; detail?: string }) => (
  <div className="rounded-md border border-border px-3 py-2">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-xl font-semibold tabular-nums">{value}</p>
    {detail && <p className="text-[11px] text-muted-foreground tabular-nums">{detail}</p>}
  </div>
);

export const FormBuilder = () => {
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState<"performance" | "builder">("performance");
  const [copied, setCopied] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const forms = config.forms ?? [];
  const form = forms.find((f) => f.id === selectedId) ?? null;
  const formToDelete = forms.find((f) => f.id === confirmDeleteId) ?? null;

  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1000 },
  });
  const activityByForm = useFormActivity(forms, contacts);

  const customSources = (config.customFields ?? [])
    .filter((f) => f.object === "contacts" && f.key)
    .map((f) => ({
      source: `custom_${f.key}`,
      label: f.label,
      type: f.type === "select" ? "select" : "text",
    }));
  const allSources = [...FIELD_SOURCES, ...customSources];

  // Local state updates instantly; the round-trip to the tenant's own
  // configuration row fires immediately for discrete actions (add/remove a
  // field, toggle required/captcha, create/delete a form) and on blur for
  // free-typed fields (form name, field labels/values, submit label,
  // success message, redirect URL, colors, corner radius, the Turnstile
  // key), so keystrokes don't spam the network. Without this, forms only
  // ever lived in the browser's local store - never actually reaching the
  // database.
  const persist = async (next: ConfigurationContextValue) => {
    try {
      await dataProvider.updateConfiguration(next);
    } catch {
      notify("Couldn't save that - you may need admin access to update forms.", {
        type: "error",
      });
    }
  };
  const saveForms = (next: SlateForm[], opts: { persistNow?: boolean } = {}) => {
    const nextConfig = { ...config, forms: next };
    updateConfig(nextConfig);
    if (opts.persistNow !== false) void persist(nextConfig);
  };
  const flushForms = () => void persist({ ...config, forms });
  const patchForm = (patch: Partial<SlateForm>, opts?: { persistNow?: boolean }) => {
    if (!form) return;
    saveForms(
      forms.map((f) => (f.id === form.id ? { ...f, ...patch } : f)),
      opts,
    );
  };

  const createForm = () => {
    const id = `${toSlug("form")}-${nextUid()}`;
    const fresh: SlateForm = {
      id,
      name: "New form",
      submitLabel: "Submit",
      successMessage: "Thanks - we'll be in touch shortly.",
      fields: [
        { id: nextUid(), source: "first_name", label: "First name", type: "text", required: true },
        { id: nextUid(), source: "email", label: "Email", type: "email", required: true },
      ],
    };
    saveForms([...forms, fresh]);
    setSelectedId(id);
  };

  const deleteForm = (id: string) => {
    saveForms(forms.filter((f) => f.id !== id));
    setSelectedId((current) => (current === id ? null : current));
    setConfirmDeleteId(null);
  };

  const addField = (source: string) => {
    if (!form) return;
    const def = allSources.find((s) => s.source === source);
    if (!def) return;
    patchForm({
      fields: [
        ...form.fields,
        {
          id: nextUid(),
          source: def.source,
          label: def.label,
          type: def.type as SlateFormField["type"],
          required: false,
        },
      ],
    });
  };

  const onDragEnd: OnDragEndResponder = ({ source, destination }) => {
    if (!form || !destination) return;
    const fields = [...form.fields];
    const [moved] = fields.splice(source.index, 1);
    fields.splice(destination.index, 0, moved!);
    patchForm({ fields });
  };

  // Same resolution TrackingPage uses for its snippet: the workspace slug
  // and services URL are only known once this build is actually deployed to
  // a tenant subdomain - the demo preview has neither.
  const workspace = getResolvedWorkspace();
  const servicesUrl = (import.meta.env.VITE_CONTROL_PLANE_URL ?? "").replace(/\/+$/, "");
  const tenantSlug =
    workspace?.slug ??
    (window.location.hostname.endsWith(".obviouscrm.com")
      ? window.location.hostname.split(".")[0]
      : undefined);
  const embedReady = Boolean(tenantSlug && servicesUrl);
  const embedCode =
    form && embedReady
      ? generateEmbedCode(form, servicesUrl, tenantSlug!, config.turnstileSiteKey)
      : "";

  // ------------------------------------------------------ list view ----
  if (!form) {
    return (
      <div className="flex flex-col gap-4">
        <Confirm
          isOpen={confirmDeleteId != null}
          title="Delete this form?"
          content={`"${formToDelete?.name ?? ""}" and its embed snippet stop working immediately. Past fills already on contacts' timelines aren't affected.`}
          confirm="Delete"
          confirmColor="warning"
          onConfirm={() => confirmDeleteId && deleteForm(confirmDeleteId)}
          onClose={() => setConfirmDeleteId(null)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-muted-foreground flex-1">
            Click a form to see its fills, who submitted, and where the data
            goes.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              createForm();
              setView("builder");
            }}
          >
            <Plus className="w-4 h-4" /> New form
          </Button>
        </div>
        {forms.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Capture leads from any site"
            body="A form drops onto any page with one embed snippet. Every fill creates or updates the contact, lands on their timeline with attribution, and can trigger workflows."
            primaryLabel="Create a form"
            onPrimary={() => {
              createForm();
              setView("builder");
            }}
          />
        ) : (
          <Card>
            <CardContent className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-3 font-medium">Form</th>
                    <th className="py-1.5 pr-3 font-medium">Fields</th>
                    <th className="py-1.5 pr-3 font-medium">Total fills</th>
                    <th className="py-1.5 pr-3 font-medium">Unique contacts</th>
                    <th className="py-1.5 pr-3 font-medium">Last 30 days</th>
                    <th className="py-1.5 pr-3 font-medium">Last fill</th>
                    <th className="py-1.5 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {forms.map((f) => {
                    const a = activityByForm.get(f.id)!;
                    return (
                      <tr
                        key={f.id}
                        className="border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/40"
                        onClick={() => {
                          setSelectedId(f.id);
                          setView("performance");
                        }}
                      >
                        <td className="py-2 pr-3 font-medium">{f.name}</td>
                        <td className="py-2 pr-3 tabular-nums text-muted-foreground">
                          {f.fields.filter((x) => x.type !== "pagebreak").length}
                        </td>
                        <td className="py-2 pr-3 tabular-nums font-semibold">
                          {a.submissions.length}
                        </td>
                        <td className="py-2 pr-3 tabular-nums">{a.uniqueContacts}</td>
                        <td className="py-2 pr-3 tabular-nums">{a.last30}</td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {shortDate(a.lastAt)}
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-6 h-6"
                              aria-label={`Delete ${f.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDeleteId(f.id);
                              }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                            <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground mt-2">
                Fill counts are sample data in this preview - once deployed,
                they reflect real submissions from your embedded forms.{" "}
                <Link to="/help/forms" className="underline hover:no-underline">
                  How forms work
                </Link>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ---------------------------------------------------- detail view ----
  const activity = activityByForm.get(form.id)!;
  return (
    <div className="flex flex-col gap-4">
      <Confirm
        isOpen={confirmDeleteId != null}
        title="Delete this form?"
        content={`"${formToDelete?.name ?? ""}" and its embed snippet stop working immediately. Past fills already on contacts' timelines aren't affected.`}
        confirm="Delete"
        confirmColor="warning"
        onConfirm={() => confirmDeleteId && deleteForm(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setSelectedId(null)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All forms
        </button>
        <h2 className="text-base font-semibold flex-1 truncate">{form.name}</h2>
        <div className="flex items-center gap-1">
          {(
            [
              ["performance", "Performance"],
              ["builder", "Builder & embed"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={`rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                view === key
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === "performance" ? (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatTile label="Total fills" value={String(activity.submissions.length)} />
            <StatTile
              label="Unique contacts"
              value={String(activity.uniqueContacts)}
              detail={
                activity.repeatFillers.length > 0
                  ? `${activity.repeatFillers.length} filled more than once`
                  : "no repeat fills"
              }
            />
            <StatTile label="Last 30 days" value={String(activity.last30)} />
            <StatTile label="Last fill" value={shortDate(activity.lastAt)} />
          </div>

          <div className="grid lg:grid-cols-3 gap-4 items-start">
            <Card className="lg:col-span-2">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  Submissions
                </p>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-card text-left text-muted-foreground">
                      <tr>
                        <th className="py-1 pr-2 font-medium">Contact</th>
                        <th className="py-1 pr-2 font-medium">When</th>
                        <th className="py-1 pr-2 font-medium">Page</th>
                        <th className="py-1 font-medium">Channel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.submissions.map((s, i) => (
                        <tr key={`${String(s.contactId)}-${i}`} className="border-t border-border">
                          <td className="py-1.5 pr-2">
                            <Link
                              to={`/contacts/${s.contactId}/show`}
                              className="underline hover:no-underline"
                            >
                              {s.name}
                            </Link>
                            {s.nth > 1 && (
                              <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                fill #{s.nth}
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 pr-2 text-xs text-muted-foreground whitespace-nowrap">
                            {shortDate(s.at)}
                          </td>
                          <td className="py-1.5 pr-2 text-xs font-mono">{s.pageUrl}</td>
                          <td className="py-1.5 text-xs text-muted-foreground">{s.channel}</td>
                        </tr>
                      ))}
                      {activity.submissions.length === 0 && (
                        <tr>
                          <td colSpan={4} className="py-3 text-xs text-muted-foreground">
                            No fills yet - embed the form to start collecting.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Sample data in the preview - at deployment every row is a
                  real form.submitted event with the page and first-touch
                  channel read from the tracking cookie.
                </p>
              </CardContent>
            </Card>

            <div className="flex flex-col gap-4">
              <Card>
                <CardContent className="p-4 flex flex-col gap-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Fills per contact
                  </p>
                  {activity.repeatFillers.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      Every submitter has filled this form once.
                    </p>
                  ) : (
                    <>
                      {activity.repeatFillers.slice(0, 6).map((r) => (
                        <Link
                          key={String(r.contactId)}
                          to={`/contacts/${r.contactId}/show`}
                          className="flex items-center gap-2 text-sm hover:underline"
                        >
                          <span className="truncate flex-1">{r.name}</span>
                          <span className="tabular-nums text-muted-foreground">
                            {r.fills} fills
                          </span>
                        </Link>
                      ))}
                      <p className="text-[11px] text-muted-foreground">
                        Everyone else has filled once ({activity.uniqueContacts -
                          activity.repeatFillers.length}{" "}
                        contacts).
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 flex flex-col gap-1.5 text-sm">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">
                    Where submissions go
                  </p>
                  <p>
                    Each fill creates the contact, or updates them if the email
                    already exists, and lands on their timeline as a form fill
                    with UTM attribution.
                  </p>
                  <p className="text-muted-foreground text-xs">
                    After submit:{" "}
                    {form.redirectUrl
                      ? `redirect to ${form.redirectUrl}`
                      : `show "${form.successMessage}"`}
                    .
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Fills also fire any workflow triggered by{" "}
                    <Link to="/marketing/workflows" className="underline hover:no-underline">
                      Form is submitted
                    </Link>
                    .
                  </p>
                  <p className="text-muted-foreground text-xs font-mono break-all">
                    POST /f/&lt;your-workspace&gt;/{form.id}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Builder */}
          <Card>
            <CardContent className="p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Input
                  value={form.name}
                  onChange={(e) => patchForm({ name: e.target.value }, { persistNow: false })}
                  onBlur={flushForms}
                  className="font-medium"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Delete form"
                  onClick={() => setConfirmDeleteId(form.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="form-fields">
                  {(dropProvided) => (
                    <div
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                      className="flex flex-col gap-2"
                    >
                      {form.fields.map((f, index) => (
                        <Draggable key={f.id} draggableId={f.id} index={index}>
                          {(dragProvided) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              style={dragProvided.draggableProps.style as React.CSSProperties}
                              className="flex items-center gap-2 border border-border rounded p-2 bg-card"
                            >
                              <span {...dragProvided.dragHandleProps}>
                                <GripVertical className="w-4 h-4 text-muted-foreground" />
                              </span>
                              <Input
                                className="flex-1"
                                value={f.label}
                                onChange={(e) =>
                                  patchForm(
                                    {
                                      fields: form.fields.map((x) =>
                                        x.id === f.id ? { ...x, label: e.target.value } : x,
                                      ),
                                    },
                                    { persistNow: false },
                                  )
                                }
                                onBlur={flushForms}
                              />
                              {(f.type === "select" || f.type === "hidden" || f.type === "text" || f.type === "email" || f.type === "phone" || f.type === "textarea") && (
                                <Input
                                  className="w-36"
                                  placeholder={
                                    f.type === "select"
                                      ? "opt1, opt2"
                                      : f.type === "hidden"
                                        ? "value"
                                        : "placeholder"
                                  }
                                  value={
                                    f.type === "select"
                                      ? (f.options ?? "")
                                      : f.type === "hidden"
                                        ? (f.value ?? "")
                                        : (f.placeholder ?? "")
                                  }
                                  onChange={(e) =>
                                    patchForm(
                                      {
                                        fields: form.fields.map((x) =>
                                          x.id === f.id
                                            ? f.type === "select"
                                              ? { ...x, options: e.target.value }
                                              : f.type === "hidden"
                                                ? { ...x, value: e.target.value }
                                                : { ...x, placeholder: e.target.value }
                                            : x,
                                        ),
                                      },
                                      { persistNow: false },
                                    )
                                  }
                                  onBlur={flushForms}
                                />
                              )}
                              <label className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Checkbox
                                  checked={f.required}
                                  onCheckedChange={(v) =>
                                    patchForm({
                                      fields: form.fields.map((x) =>
                                        x.id === f.id ? { ...x, required: v === true } : x,
                                      ),
                                    })
                                  }
                                />
                                required
                              </label>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Remove field"
                                onClick={() =>
                                  patchForm({
                                    fields: form.fields.filter((x) => x.id !== f.id),
                                  })
                                }
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {dropProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              <div className="flex items-center gap-2">
                <Select value="" onValueChange={addField}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="＋ Add a field…" />
                  </SelectTrigger>
                  <SelectContent>
                    {allSources.map((s) => (
                      <SelectItem key={s.source} value={s.source}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Button label</p>
                  <Input
                    value={form.submitLabel}
                    onChange={(e) =>
                      patchForm({ submitLabel: e.target.value }, { persistNow: false })
                    }
                    onBlur={flushForms}
                  />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Success message</p>
                  <Input
                    value={form.successMessage}
                    onChange={(e) =>
                      patchForm({ successMessage: e.target.value }, { persistNow: false })
                    }
                    onBlur={flushForms}
                  />
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  Redirect after submit (optional - overrides the message)
                </p>
                <Input
                  placeholder="https://yoursite.com/thank-you"
                  value={form.redirectUrl ?? ""}
                  onChange={(e) =>
                    patchForm({ redirectUrl: e.target.value }, { persistNow: false })
                  }
                  onBlur={flushForms}
                />
              </div>
              <div className="border-t pt-3 flex flex-col gap-2">
                <p className="text-sm font-medium">Spam protection</p>
                <p className="text-xs text-muted-foreground">
                  A honeypot field is always included - invisible to people,
                  it silently drops bot submissions.
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={!!form.captcha}
                    onCheckedChange={(v) => patchForm({ captcha: v === true })}
                  />
                  Also require CAPTCHA (Cloudflare Turnstile - invisible for
                  most visitors)
                </label>
                {form.captcha && (
                  <Input
                    placeholder="Turnstile site key (free at cloudflare.com/turnstile)"
                    value={config.turnstileSiteKey ?? ""}
                    onChange={(e) => updateConfig({ ...config, turnstileSiteKey: e.target.value })}
                    onBlur={() => void persist(config)}
                  />
                )}
              </div>
              <div className="border-t pt-3 flex flex-col gap-2">
                <p className="text-sm font-medium">Design</p>
                <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                  <label className="flex items-center gap-1">
                    button
                    <input
                      type="color"
                      aria-label="Button color"
                      className="h-6 w-8 rounded border border-border bg-transparent"
                      value={form.design?.buttonColor ?? "#334155"}
                      onChange={(e) =>
                        patchForm(
                          { design: { ...form.design, buttonColor: e.target.value } },
                          { persistNow: false },
                        )
                      }
                      onBlur={flushForms}
                    />
                  </label>
                  <label className="flex items-center gap-1">
                    button text
                    <input
                      type="color"
                      aria-label="Button text color"
                      className="h-6 w-8 rounded border border-border bg-transparent"
                      value={form.design?.buttonTextColor ?? "#ffffff"}
                      onChange={(e) =>
                        patchForm(
                          { design: { ...form.design, buttonTextColor: e.target.value } },
                          { persistNow: false },
                        )
                      }
                      onBlur={flushForms}
                    />
                  </label>
                  <label className="flex items-center gap-1">
                    corners
                    <Input
                      type="number"
                      className="w-16 h-7"
                      min={0}
                      max={24}
                      value={form.design?.inputRadius ?? 4}
                      onChange={(e) =>
                        patchForm(
                          {
                            design: { ...form.design, inputRadius: Number(e.target.value) || 0 },
                          },
                          { persistNow: false },
                        )
                      }
                      onBlur={flushForms}
                    />
                    px
                  </label>
                  <label className="flex items-center gap-1">
                    font
                    <select
                      aria-label="Form font"
                      className="h-7 rounded border border-border bg-transparent px-1"
                      value={form.design?.font ?? "inherit"}
                      onChange={(e) =>
                        patchForm({
                          design: {
                            ...form.design,
                            font: e.target.value as NonNullable<SlateForm["design"]>["font"],
                          },
                        })
                      }
                    >
                      <option value="inherit">match the site</option>
                      <option value="system">system sans</option>
                      <option value="georgia">Georgia serif</option>
                    </select>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview + embed */}
          <div className="flex flex-col gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">
                  Preview
                </p>
                <div
                  className="max-w-sm flex flex-col gap-2"
                  style={{
                    fontFamily:
                      form.design?.font === "georgia"
                        ? "Georgia, serif"
                        : form.design?.font === "system"
                          ? "system-ui, sans-serif"
                          : undefined,
                  }}
                >
                  {form.fields.map((f) =>
                    f.type === "pagebreak" ? (
                      <div key={f.id} className="flex items-center gap-2 my-2">
                        <div className="flex-1 border-t border-dashed border-border" />
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          next page
                        </span>
                        <div className="flex-1 border-t border-dashed border-border" />
                      </div>
                    ) : f.type === "hidden" ? null : (
                      <div key={f.id}>
                        <p className="text-sm mb-1">
                          {f.label}
                          {f.required && " *"}
                        </p>
                        {f.type === "textarea" ? (
                          <Textarea disabled rows={3} style={{ borderRadius: form.design?.inputRadius ?? 4 }} />
                        ) : f.type === "checkbox" ? (
                          <Checkbox disabled />
                        ) : (
                          <Input
                            disabled
                            placeholder={f.placeholder}
                            style={{ borderRadius: form.design?.inputRadius ?? 4 }}
                          />
                        )}
                      </div>
                    ),
                  )}
                  <Button
                    className="mt-2 w-fit"
                    disabled
                    style={{
                      background: form.design?.buttonColor ?? "#334155",
                      color: form.design?.buttonTextColor ?? "#ffffff",
                      borderRadius: form.design?.inputRadius ?? 4,
                    }}
                  >
                    {form.submitLabel}
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                {embedReady ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        Embed code
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard?.writeText(embedCode);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 1500);
                        }}
                      >
                        <Copy className="w-4 h-4" /> {copied ? "Copied" : "Copy"}
                      </Button>
                    </div>
                    <Textarea
                      readOnly
                      value={embedCode}
                      rows={12}
                      className="font-mono text-xs"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Drop this on any site, ready to paste - your workspace
                      and endpoint are already filled in. Submissions match or
                      create a contact automatically, with UTM attribution
                      attached.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground max-w-prose">
                    The embed code is generated per workspace and is available
                    in deployed workspaces only - the demo preview has no site
                    of its own to embed on.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};
